<div class="header-bar">
  <div class="header-left">
    <div class="header-text">
      Flow Test
      <span *ngIf="test">#{{ test.id }}</span>
    </div>
  </div>
  <div class="header-center"></div>
  <div class="header-right">
    <button mat-button color="plain" (click)="back()">Back</button>
    <button mat-button color="lightBlue" (click)="rerun()" [disabled]="!test">Re-run</button>
    <button mat-button color="red" (click)="remove()" [disabled]="!test">Delete</button>
  </div>
</div>

<div class="flow-test-detail">
  <div class="container" *ngIf="!isLoading && test; else loadingOrErrorTpl">
    <div class="container-content">
      <div class="meta">
        <div class="row">
          <span class="label">Status:</span>
          <span class="status-pill status-{{ test.status }}">{{ test.status }}</span>
          <span class="polling-indicator" *ngIf="!isTerminal()">
            Polling every 3s…
          </span>
        </div>
        <div class="row">
          <span class="label">Flow:</span>
          <span>{{ test.logic_tree?.name || 'Flow #' + test.logic_tree_id }}</span>
        </div>
        <div class="row">
          <span class="label">Created:</span>
          <span>{{ test.created_at | date: 'medium' }}</span>
        </div>
        <div class="row" *ngIf="test.queued_at">
          <span class="label">Queued:</span>
          <span>{{ test.queued_at | date: 'medium' }}</span>
        </div>
        <div class="row" *ngIf="test.started_at">
          <span class="label">Started:</span>
          <span>{{ test.started_at | date: 'medium' }}</span>
        </div>
        <div class="row" *ngIf="test.completed_at">
          <span class="label">Completed:</span>
          <span>{{ test.completed_at | date: 'medium' }}</span>
        </div>
        <div class="row" *ngIf="test.error">
          <span class="label">Error:</span>
          <span class="error">{{ test.error }}</span>
        </div>
      </div>

      <section>
        <h3>Config</h3>
        <pre>{{ test.config | json }}</pre>
      </section>

      <section *ngIf="isTerminal()">
        <h3>Session Trace</h3>
        <div *ngIf="traceSteps.length === 0" class="empty">
          No history recorded.
        </div>
        <ol *ngIf="traceSteps.length > 0" class="trace">
          <li
            *ngFor="let step of traceSteps; trackBy: trackStep"
            [ngClass]="'trace-' + step.kind"
          >
            <ng-container *ngIf="step.kind === 'history'">
              <div class="trace-head">
                <span class="trace-badge history">Traversed</span>
                <code>{{ step.source }} → {{ step.target }}</code>
                <span class="muted" *ngIf="step.created_at">{{ step.created_at | date: 'mediumTime' }}</span>
              </div>
              <pre *ngIf="step.payload">{{ step.payload | json }}</pre>
            </ng-container>

            <ng-container *ngIf="step.kind === 'ai_eval'">
              <div class="trace-head">
                <span
                  class="trace-badge ai"
                  [class.verdict-true]="step.truthy"
                  [class.verdict-false]="!step.truthy"
                >
                  AI · {{ step.truthy ? 'TRUE' : 'FALSE' }}
                </span>
                <code>{{ step.edge_name || step.edge_id || '(edge)' }}</code>
                <span class="muted" *ngIf="step.created_at">{{ step.created_at | date: 'mediumTime' }}</span>
              </div>
              <div class="ai-summary">{{ step.summary || step.error || '(no summary)' }}</div>
              <button
                type="button"
                class="trace-toggle"
                (click)="toggleStep(step.key)"
              >
                {{ isStepExpanded(step.key) ? '▾ Hide details' : '▸ Show details' }}
              </button>
              <dl class="ai-detail" *ngIf="isStepExpanded(step.key)">
                <dt>Prompt</dt>
                <dd><code>{{ step.prompt || '(none)' }}</code></dd>
                <dt>Edge</dt>
                <dd><code>{{ step.source }} → {{ step.target }}</code></dd>
                <dt>Model</dt>
                <dd>{{ step.model || '—' }}</dd>
                <dt>API</dt>
                <dd>{{ step.api_used || '—' }}</dd>
                <dt>Web search</dt>
                <dd>
                  <ng-container *ngIf="!step.web_search_available">not offered</ng-container>
                  <ng-container *ngIf="step.web_search_available && (step.web_search_calls == null)">offered (usage unknown)</ng-container>
                  <ng-container *ngIf="step.web_search_available && step.web_search_calls === 0">offered, but model did not search</ng-container>
                  <ng-container *ngIf="step.web_search_available && step.web_search_calls > 0">searched {{ step.web_search_calls }}×</ng-container>
                </dd>
                <ng-container *ngIf="step.latency_ms != null">
                  <dt>Latency</dt>
                  <dd>{{ step.latency_ms }} ms</dd>
                </ng-container>
                <ng-container *ngIf="step.raw">
                  <dt>Raw</dt>
                  <dd><pre>{{ step.raw }}</pre></dd>
                </ng-container>
              </dl>
            </ng-container>
          </li>
        </ol>
      </section>

      <section *ngIf="isTerminal()">
        <h3>Captured Outbound</h3>
        <div *ngIf="capturedOutbound().length === 0" class="empty">
          No outbound communications were captured for this run.
        </div>
        <ul *ngIf="capturedOutbound().length > 0" class="captures">
          <li *ngFor="let cap of capturedOutbound()">
            <div class="cap-head">
              <strong>{{ cap.provider }}</strong>
              <span class="muted">{{ cap.captured_at | date: 'medium' }}</span>
            </div>
            <div class="cap-summary">{{ captureSummary(cap) }}</div>
            <details>
              <summary>Payload</summary>
              <pre>{{ cap | json }}</pre>
            </details>
          </li>
        </ul>
      </section>

      <section *ngIf="isTerminal()">
        <h3>Raw Result</h3>
        <pre>{{ resultSummary() | json }}</pre>
      </section>
    </div>
  </div>
</div>

<ng-template #loadingOrErrorTpl>
  <div class="loading" *ngIf="isLoading">Loading…</div>
  <div class="error" *ngIf="!isLoading && errorMessage">{{ errorMessage }}</div>
</ng-template>
